'use client'

// Modal de exportación de .cfg — la salida para el modo web, donde no podemos
// escribir en la carpeta del juego (ver utils/bridge.js: en el navegador
// installConfigsToGame/launchCS2 son no-ops declarados).
//
// Deja elegir qué secciones exportar, ver el .cfg resultante antes de bajarlo,
// y descargar todo junto o cada sección como archivo suelto.

import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import {
    Download,
    Visibility,
    VisibilityOff,
    InsertDriveFile,
    ContentCopy,
    Check,
} from '@mui/icons-material'
import Modal from './Modal'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'

// Todas las secciones que utils/configGenerator.js sabe emitir. El orden es el
// de lectura del .cfg, no el del generador.
const SECTIONS = [
    { key: 'crosshair', labelKey: 'EXPORT_SECTION_CROSSHAIR', file: 'crosshair' },
    { key: 'mouse', labelKey: 'EXPORT_SECTION_MOUSE', file: 'mouse' },
    { key: 'viewmodel', labelKey: 'EXPORT_SECTION_VIEWMODEL', file: 'viewmodel' },
    { key: 'hud', labelKey: 'EXPORT_SECTION_HUD', file: 'hud' },
    { key: 'binds_basic', labelKey: 'EXPORT_SECTION_BINDS_BASIC', file: 'binds_basic' },
    { key: 'binds_special', labelKey: 'EXPORT_SECTION_BINDS_SPECIAL', file: 'binds_special' },
    { key: 'binds_custom', labelKey: 'EXPORT_SECTION_BINDS_CUSTOM', file: 'binds_custom' },
    { key: 'radio', labelKey: 'EXPORT_SECTION_RADIO', file: 'radio' },
    { key: 'graphics', labelKey: 'EXPORT_SECTION_GRAPHICS', file: 'graphics' },
    { key: 'video', labelKey: 'EXPORT_SECTION_VIDEO', file: 'video' },
    { key: 'practice', labelKey: 'EXPORT_SECTION_PRACTICE', file: 'practice' },
    { key: 'custom_scripts', labelKey: 'EXPORT_SECTION_SCRIPTS', file: 'scripts' },
]

// El generador siempre antepone esta cabecera. Para saber si una sección
// aporta algo hay que mirar lo que queda DESPUÉS de sacarla.
const GENERATOR_HEADER = '// CONFIG-STRIKE - config-strike.com'

function hasRealContent(cfg) {
    return cfg
        .split('\n')
        .filter((l) => l.trim() && !l.trim().startsWith('//'))
        .length > 0
}

function safeFileName(name) {
    return (name || 'config').replace(/[^a-zA-Z0-9_-]/g, '_')
}

function downloadText(fileName, text) {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    // Sin esto el blob queda retenido hasta que se cierre la pestaña.
    setTimeout(() => URL.revokeObjectURL(url), 0)
}

// El ModalContainer es un flex column sin align-items propio, así que hereda
// el `align-items: flex-start` del `div {}` global de globalStyles.js y sus
// hijos se encogen al contenido. De ahí el width:100% explícito en todo lo
// que va directo adentro del modal: sin eso las grillas colapsan a 1 columna.
//
// El padding también es nuestro: Modal.jsx sólo se lo pone al título, el
// cuerpo va a sangre. Los 24px de los lados igualan al del título.
const Body = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 4px 24px 24px;
`

const Intro = styled.p`
    width: 100%;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    line-height: 1.6;
    margin: 0 0 18px;
`

const Block = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
`

const BlockTitle = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 10px;
`

const MiniLink = styled.button`
    background: none;
    border: none;
    color: rgba(129, 140, 248, 0.9);
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    padding: 0;

    &:hover { color: #fff; }
`

const SectionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 8px;
`

const SectionRow = styled.label`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 8px;
    border: 1px solid ${({ $on }) => ($on ? 'rgba(99,102,241,0.45)' : 'rgba(255,255,255,0.07)')};
    background: ${({ $on }) => ($on ? 'rgba(99,102,241,0.10)' : 'rgba(255,255,255,0.02)')};
    font-size: 13px;
    color: ${({ $on }) => ($on ? '#fff' : 'rgba(255,255,255,0.55)')};
    cursor: pointer;
    user-select: none;
    transition: all 0.15s;

    &:hover { border-color: rgba(99, 102, 241, 0.6); }

    input {
        accent-color: #6366f1;
        cursor: pointer;
        flex: none;
    }
`

const Preview = styled.pre`
    margin: 0;
    max-height: 280px;
    overflow: auto;
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 14px 16px;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 12px;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.8);
    white-space: pre;
`

const FileGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
    gap: 10px;
`

const FileCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 8px 11px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.02);
    cursor: pointer;
    transition: all 0.18s;

    &:hover {
        border-color: rgba(99, 102, 241, 0.55);
        background: rgba(99, 102, 241, 0.1);
        transform: translateY(-2px);
    }

    svg { font-size: 30px; color: #818cf8; }
`

const FileName = styled.span`
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 10.5px;
    color: rgba(255, 255, 255, 0.65);
    text-align: center;
    word-break: break-all;
    line-height: 1.35;
`

const Actions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 4px;
`

const PrimaryButton = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 20px;
    border-radius: 9px;
    border: none;
    background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
    color: #fff;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.12); }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
    svg { font-size: 17px; }
`

const GhostButton = styled(PrimaryButton)`
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.7);

    &:hover:not(:disabled) {
        color: #fff;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(99, 102, 241, 0.12);
        filter: none;
    }
`

const Empty = styled.div`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.4);
    padding: 10px 0;
`

export default function ExportCfgModal({ isOpen, onClose }) {
    const { t } = useLanguage()
    const { generateConfigString, configData, activeModules, configName } = useConfig()

    const [selected, setSelected] = useState(null) // null = todavía sin tocar
    const [showPreview, setShowPreview] = useState(false)
    const [copied, setCopied] = useState(false)

    // Una sección se ofrece sólo si realmente produce comandos con los datos
    // actuales: mostrar "Scripts" cuando el usuario no escribió ninguno sería
    // ofrecerle un archivo vacío.
    const available = useMemo(() => {
        if (!configData) return []
        return SECTIONS.map((s) => {
            const cfg = generateConfigString({ format: 'display' }, { ...configData, _meta: { activeModules: [s.key] } })
            const body = cfg.replace(GENERATOR_HEADER, '').trim()
            return { ...s, cfg, hasContent: hasRealContent(body) }
        }).filter((s) => s.hasContent)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configData, generateConfigString, isOpen])

    // Por defecto vienen tildadas las secciones activas de la config; si no hay
    // ninguna marcada como activa, se tildan todas las que tengan contenido.
    const checked = useMemo(() => {
        if (selected) return selected
        const fromActive = available.filter((s) => (activeModules || []).includes(s.key)).map((s) => s.key)
        return fromActive.length ? fromActive : available.map((s) => s.key)
    }, [selected, available, activeModules])

    const toggle = (key) => {
        setSelected(checked.includes(key) ? checked.filter((k) => k !== key) : [...checked, key])
    }

    const combined = useMemo(
        () => generateConfigString({ format: 'display' }, { ...configData, _meta: { activeModules: checked } }),
        [configData, checked, generateConfigString],
    )

    const baseName = safeFileName(configName)
    const chosen = available.filter((s) => checked.includes(s.key))

    const copyAll = () => {
        navigator.clipboard?.writeText(combined)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('EXPORT_MODAL_TITLE')} width="720px" maxWidth="94vw">
            <Body>
            <Intro>{t('EXPORT_MODAL_INTRO')}</Intro>

            <Block>
                <BlockTitle>
                    <span>{t('EXPORT_MODAL_SECTIONS')}</span>
                    {available.length > 0 && (
                        <MiniLink
                            type="button"
                            onClick={() => setSelected(checked.length === available.length ? [] : available.map((s) => s.key))}
                        >
                            {checked.length === available.length ? t('EXPORT_MODAL_NONE') : t('EXPORT_MODAL_ALL')}
                        </MiniLink>
                    )}
                </BlockTitle>

                {available.length === 0 ? (
                    <Empty>{t('EXPORT_MODAL_NOTHING')}</Empty>
                ) : (
                    <SectionGrid>
                        {available.map((s) => (
                            <SectionRow key={s.key} $on={checked.includes(s.key)}>
                                <input
                                    type="checkbox"
                                    checked={checked.includes(s.key)}
                                    onChange={() => toggle(s.key)}
                                />
                                {t(s.labelKey)}
                            </SectionRow>
                        ))}
                    </SectionGrid>
                )}
            </Block>

            <Block>
                <Actions>
                    <PrimaryButton
                        type="button"
                        disabled={!checked.length}
                        onClick={() => downloadText(`configstrike_${baseName}.cfg`, combined)}
                    >
                        <Download />
                        {t('EXPORT_MODAL_DOWNLOAD')}
                    </PrimaryButton>
                    <GhostButton type="button" disabled={!checked.length} onClick={() => setShowPreview((v) => !v)}>
                        {showPreview ? <VisibilityOff /> : <Visibility />}
                        {showPreview ? t('EXPORT_MODAL_HIDE') : t('EXPORT_MODAL_VIEW')}
                    </GhostButton>
                    <GhostButton type="button" disabled={!checked.length} onClick={copyAll}>
                        {copied ? <Check /> : <ContentCopy />}
                        {copied ? t('COPIED') : t('EXPORT_MODAL_COPY')}
                    </GhostButton>
                </Actions>
            </Block>

            {showPreview && checked.length > 0 && (
                <Block>
                    <BlockTitle><span>configstrike_{baseName}.cfg</span></BlockTitle>
                    <Preview>{combined}</Preview>
                </Block>
            )}

            {/* Descarga suelta por sección: sólo tiene sentido si hay más de una. */}
            {chosen.length > 1 && (
                <Block>
                    <BlockTitle><span>{t('EXPORT_MODAL_INDIVIDUAL')}</span></BlockTitle>
                    <FileGrid>
                        {chosen.map((s) => (
                            <FileCard
                                key={s.key}
                                type="button"
                                title={t('EXPORT_MODAL_DOWNLOAD_ONE')}
                                onClick={() => downloadText(`${baseName}_${s.file}.cfg`, s.cfg)}
                            >
                                <InsertDriveFile />
                                <FileName>{s.file}.cfg</FileName>
                            </FileCard>
                        ))}
                    </FileGrid>
                </Block>
            )}
            </Body>
        </Modal>
    )
}
